// Popup de montagem de pizza no cardápio público — fluxo WIZARD (etapa por etapa).
// 1. Tamanho → 2. Sabores → [3. Borda] → Finalizar
// Mesma UX dos combos (header "Etapa X de Y" + back/forward).

import { useState, useEffect, useMemo, useCallback } from "react";
import { X, Check, Plus, Minus, Pizza, ImageOff, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { miniaturaDoProduto } from "../../utils/miniatura";
import { linhasDaEtapa, sinalDaEtapa, valorDaEtapa } from "../../utils/precoEtapas";

const fmtR$ = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `/api${url}`;
  return url;
};
// Sabores e adicionais em miniatura de 400 px — a mesma da grade do cardápio,
// que o navegador já guardou (25/09/2026). A foto grande do resumo segue inteira.
const fotoPequena = (url) => miniaturaDoProduto(url, 400) || getImageUrl(url);

// Fração visual: 1, 1/2, 1/3, 1/4...
const fracLabel = (n) => (n <= 1 ? "1" : `1/${n}`);

export default function PizzaPopup({
  open,
  onClose,
  primaryFlavor,
  allFlavors,
  allProducts = [],
  config,
  darkMode,
  onAddToCart,
}) {
  const t = useMemo(() => ({
    bg: darkMode ? "bg-zinc-900" : "bg-gray-50",
    bgCard: darkMode ? "bg-zinc-800" : "bg-white",
    text: darkMode ? "text-white" : "text-gray-900",
    textMuted: darkMode ? "text-zinc-400" : "text-gray-500",
    textMuted2: darkMode ? "text-zinc-500" : "text-gray-400",
    border: darkMode ? "border-zinc-700" : "border-gray-200",
  }), [darkMode]);

  const sizes = useMemo(() => (config?.sizes || []).filter(s => s.is_active !== 0), [config]);
  const borders = useMemo(() => (config?.borders || []).filter(b => b.is_active !== 0), [config]);
  const pricingRule = config?.pricing_rule || "highest";

  // Etapas adicionais (order_steps) configuradas no produto via Produto → Personalização
  // Ex: "Deseja algum adicional?" com itens Muçarela/Catupiry/etc.
  // Cada etapa: { name, min, max, calculation_type ('soma'|'maior'), sync_prices, items: [{product_id, price_override}] }
  // Ignoramos combo_only (pizza não tem combo/simples)
  const customSteps = useMemo(() => {
    // order_steps pode vir como array OU string JSON serializada, dependendo do endpoint
    let raw = primaryFlavor?.order_steps;
    if (typeof raw === "string") {
      try { raw = JSON.parse(raw); } catch { raw = []; }
    }
    if (!Array.isArray(raw)) return [];
    // FILTRA steps pizza_* — eles já têm UI nativa do wizard (size/flavors/border).
    // Sem este filtro, eles vazam pra "Etapa custom" depois da Borda, e o counter
    // do header (linha 406+) renderiza "Escolha no mínimo X · N / ∞" no lugar do
    // texto certo de Sabores ("Escolha até 2 sabores · 1 / 2").
    return raw.filter(s =>
      s
      && Array.isArray(s.items)
      && s.items.length > 0
      && !String(s.kind || "").startsWith("pizza_")
    );
  }, [primaryFlavor]);

  // Etapas dinâmicas do wizard
  const stepsDef = useMemo(() => {
    const out = [
      { id: "size", label: "Tamanho" },
      { id: "flavors", label: "Sabores" },
    ];
    if (borders.length > 0) out.push({ id: "border", label: "Borda" });
    customSteps.forEach((s, i) => {
      out.push({ id: `custom-${i}`, label: s.name || `Etapa ${i + 1}`, customIdx: i });
    });
    out.push({ id: "review", label: "Finalizar" });
    return out;
  }, [borders.length, customSteps]);

  const [stepIdx, setStepIdx] = useState(0);
  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [selectedFlavorIds, setSelectedFlavorIds] = useState([]);
  const [selectedBorderId, setSelectedBorderId] = useState(null);
  // Map de idx-da-etapa → array de product_ids selecionados
  const [stepSelections, setStepSelections] = useState({});
  const [observation, setObservation] = useState("");
  const [quantity, setQuantity] = useState(1);

  // Reset ao abrir
  useEffect(() => {
    if (!open) return;
    setStepIdx(0);
    setSelectedSizeId(null);
    setSelectedFlavorIds(primaryFlavor?.id ? [primaryFlavor.id] : []);
    setSelectedBorderId(null);
    setStepSelections({});
    setObservation("");
    setQuantity(1);
  }, [open, primaryFlavor?.id]);

  // (focus trap escape agora é resolvido pelo <dialog> nativo no top layer — ver usePizzaPopup.jsx)

  // Helpers para etapas custom: resolve preço/foto do item (sync com /products)
  const productById = useCallback((pid) => allProducts.find(p => String(p.id) === String(pid)), [allProducts]);
  // A lista recebida pode ainda não ter o produto da etapa (a tela carrega a lista
  // de venda, sem insumos, antes da completa). Sem ele, nome e preço vêm do que o
  // próprio cadastro da etapa guarda (product_name / price_override) — antes caía
  // em "Item" e o atendente via "Item" em todas as opções (achado 522).
  const getItemPrice = useCallback((item, step) => {
    const p = productById(item.product_id);
    if (step?.sync_prices !== false && p) return Number(p.sale_price || 0);
    if (item.price_override === 0) return 0;
    if (item.price_override > 0) return Number(item.price_override);
    return Number(p?.sale_price || 0);
  }, [productById]);
  const getItemName = useCallback((item) => {
    const p = productById(item.product_id);
    return p?.name || item.product_name || item.name || item.label || "Item";
  }, [productById]);
  const getItemDescription = useCallback((item) => {
    const p = productById(item.product_id);
    return p?.description || item.description || "";
  }, [productById]);
  const getItemPhoto = useCallback((item) => {
    const p = productById(item.product_id);
    return p?.photo_url || item.photo_url || null;
  }, [productById]);
  const isItemAvailable = useCallback((item) => {
    const p = productById(item.product_id);
    if (!p) return true; // sem lookup: mostra mesmo assim (não esconde por falta de dados)
    return p.available !== false; // hidden=true é OK aqui — adicionais normalmente ficam ocultos do cardápio principal
  }, [productById]);

  // Helpers de limites da etapa (suportam ambos os formatos do banco: min/max ou min_selections/max_selections)
  const stepMin = (step) => Math.max(0, Number(step?.min_selections ?? step?.min ?? 0));
  // max_selections = 0 significa ILIMITADO (convenção do ProductPopup)
  const stepMax = (step) => {
    const raw = step?.max_selections ?? step?.max;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return Infinity;
    return n;
  };
  const isSingleChoice = (step) => stepMax(step) === 1;

  // v1.5.399 — quantidade por item nas etapas custom.
  // `stepSelections[idx]` é um array de product_ids que PODE repetir: 2x Ketchup
  // = [ketchup, ketchup]. Mesma convenção do ProductPopup (cardápio não-pizza),
  // então o payload de subitems e o cálculo de preço saem consistentes entre os
  // dois fluxos. Item só ganha stepper quando `allow_quantity` está ligado no
  // cadastro; sem isso continua sendo checkbox/radio como antes.
  // Quantidade NÃO se aplica a: etapa single-choice (radio) nem a etapa com
  // calculation_type="maior" — nesta o preço cobrado é só o do item mais caro,
  // então declarar 3x no pedido divergiria do valor efetivamente cobrado.
  const allowsQty = (step, item) =>
    !!item?.allow_quantity && stepMax(step) !== 1 && step?.calculation_type !== "maior";
  const itemMaxQty = (item) => {
    const n = Number(item?.max_qty);
    return Number.isFinite(n) && n > 0 ? n : Infinity;
  };
  const qtyOf = (customIdx, productId) =>
    (stepSelections[customIdx] || []).filter(id => String(id) === String(productId)).length;

  // Toggle / set seleção em etapa custom
  const toggleStepItem = useCallback((customIdx, productId) => {
    const step = customSteps[customIdx];
    if (!step) return;
    const max = stepMax(step);
    setStepSelections(prev => {
      const cur = prev[customIdx] || [];
      const has = cur.includes(productId);
      let next;
      if (has) {
        // remove TODAS as ocorrências (desmarcar item com qty > 1 zera o item)
        next = cur.filter(id => String(id) !== String(productId));
      } else if (max === 1) {
        // single-choice: substitui
        next = [productId];
      } else {
        if (cur.length >= max) {
          toast.error(`Você já selecionou o máximo de ${max}`);
          return prev;
        }
        next = [...cur, productId];
      }
      return { ...prev, [customIdx]: next };
    });
  }, [customSteps]);

  // Incrementa 1 unidade do item (respeita max da etapa e max_qty do item)
  const incStepItem = useCallback((customIdx, productId) => {
    const step = customSteps[customIdx];
    if (!step) return;
    const item = (step.items || []).find(i => String(i.product_id) === String(productId));
    if (!item) return;
    const max = stepMax(step);
    const maxQty = itemMaxQty(item);
    setStepSelections(prev => {
      const cur = prev[customIdx] || [];
      if (cur.length >= max) {
        toast.error(`Você já selecionou o máximo de ${max}`);
        return prev;
      }
      const atual = cur.filter(id => String(id) === String(productId)).length;
      if (atual >= maxQty) {
        toast.error(`Máximo de ${maxQty} por item`);
        return prev;
      }
      return { ...prev, [customIdx]: [...cur, productId] };
    });
  }, [customSteps]);

  // Remove 1 unidade do item
  const decStepItem = useCallback((customIdx, productId) => {
    setStepSelections(prev => {
      const cur = prev[customIdx] || [];
      const idx = cur.map(String).lastIndexOf(String(productId));
      if (idx < 0) return prev;
      return { ...prev, [customIdx]: [...cur.slice(0, idx), ...cur.slice(idx + 1)] };
    });
  }, []);

  // Preço extra das etapas custom (respeita calculation_type)
  const customExtra = useMemo(() => {
    let total = 0;
    customSteps.forEach((step, idx) => {
      const sel = stepSelections[idx] || [];
      if (sel.length === 0) return;
      // `sel` pode repetir o mesmo id (quantidade, v1.5.399) — o map preserva
      // as repetições, então a soma já multiplica pelo qty naturalmente.
      const items = sel.map(pid => step.items.find(i => String(i.product_id) === String(pid))).filter(Boolean);
      // Soma, Máximo ou Subtração: regra única em utils/precoEtapas.js
      total += valorDaEtapa(step, items.map(it => getItemPrice(it, step)));
    });
    return total;
  }, [customSteps, stepSelections, getItemPrice]);

  const selectedSize = useMemo(
    () => sizes.find(s => s.id === selectedSizeId) || null,
    [sizes, selectedSizeId]
  );
  const maxFlavors = selectedSize?.max_flavors || 1;

  // Ajuste defensivo quando troca tamanho — não permite mais sabores que max
  useEffect(() => {
    if (!selectedSize) return;
    setSelectedFlavorIds(prev => {
      if (prev.length <= maxFlavors) return prev;
      const primary = primaryFlavor?.id;
      const rest = prev.filter(id => id !== primary).slice(0, Math.max(0, maxFlavors - 1));
      return primary ? [primary, ...rest].slice(0, maxFlavors) : rest.slice(0, maxFlavors);
    });
  }, [selectedSizeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const flavorPriceFor = (fid) => {
    const f = allFlavors.find(x => x.id === fid);
    return Number(f?.pizza_prices?.[selectedSizeId] || 0);
  };
  const selectedFlavorPrices = selectedFlavorIds.map(flavorPriceFor);
  const flavorsPrice = (() => {
    if (selectedFlavorPrices.length === 0) return 0;
    if (pricingRule === "average") {
      return selectedFlavorPrices.reduce((s, v) => s + v, 0) / selectedFlavorPrices.length;
    }
    if (pricingRule === "lowest") {
      return Math.min(...selectedFlavorPrices);
    }
    return Math.max(...selectedFlavorPrices);
  })();

  const borderInfo = borders.find(b => b.id === selectedBorderId) || null;
  const borderPrice = Number(borderInfo?.prices?.[selectedSizeId] || 0);

  const totalUnit = flavorsPrice + borderPrice + customExtra;
  const totalCart = totalUnit * quantity;

  const toggleFlavor = (fid) => {
    if (!selectedSizeId) return;
    setSelectedFlavorIds(prev => {
      if (prev.includes(fid)) {
        if (prev.length === 1) return prev;
        return prev.filter(x => x !== fid);
      }
      if (prev.length >= maxFlavors) {
        toast.error(`Este tamanho permite no máximo ${maxFlavors} ${maxFlavors === 1 ? "sabor" : "sabores"}`);
        return prev;
      }
      return [...prev, fid];
    });
  };

  // Pode avançar pra próxima etapa?
  const currentStep = stepsDef[stepIdx];
  const canAdvance = useMemo(() => {
    if (!currentStep) return false;
    if (currentStep.id === "size") return !!selectedSizeId;
    if (currentStep.id === "flavors") return selectedFlavorIds.length > 0 && totalUnit > 0;
    if (currentStep.id === "border") return true; // borda é opcional
    if (currentStep.id.startsWith("custom-")) {
      const step = customSteps[currentStep.customIdx];
      if (!step) return true;
      const sel = stepSelections[currentStep.customIdx] || [];
      return sel.length >= stepMin(step);
    }
    return false;
  }, [currentStep, selectedSizeId, selectedFlavorIds.length, totalUnit, customSteps, stepSelections]);

  const goNext = useCallback(() => {
    if (!canAdvance) return;
    setStepIdx(i => Math.min(stepsDef.length - 1, i + 1));
  }, [canAdvance, stepsDef.length]);

  const goBack = useCallback(() => {
    if (stepIdx === 0) {
      onClose();
      return;
    }
    setStepIdx(i => Math.max(0, i - 1));
  }, [stepIdx, onClose]);

  const handleAdd = () => {
    if (!selectedSize) { toast.error("Selecione o tamanho"); return; }
    if (selectedFlavorIds.length === 0) { toast.error("Selecione ao menos 1 sabor"); return; }
    if (totalUnit <= 0) { toast.error("Este sabor ainda não tem preço para esse tamanho"); return; }
    const flavorNames = selectedFlavorIds.map(fid => allFlavors.find(f => f.id === fid)?.name || "?");
    const subitems = [];
    selectedFlavorIds.forEach(fid => {
      const f = allFlavors.find(x => x.id === fid);
      subitems.push({
        product_id: fid,                          // sabor é um produto — pra lookup de foto no carrinho
        // Um sabor só sai sem o "1" na frente ("4 Queijos", não "1 4 Queijos"),
        // igual ao cardápio online — a fração só aparece na pizza meio a meio
        nome: selectedFlavorIds.length > 1 ? `${fracLabel(selectedFlavorIds.length)} ${f?.name || "?"}` : (f?.name || "?"),
        photo_url: f?.photo_url || null,          // foto já vem do pizzaConfig.flavors
        preco: 0,
        step_name: "Sabores",
      });
    });
    if (borderInfo) {
      subitems.push({ nome: borderInfo.name, preco: borderPrice, step_name: "Borda" });
    }
    // Sub-items das etapas custom (Adicionais/Acompanhamentos/etc.) configuradas no produto
    customSteps.forEach((step, idx) => {
      const sel = stepSelections[idx] || [];
      // v1.5.399 — consolida repetições do mesmo id em UMA linha com
      // `quantidade` (2x Ketchup = 1 subitem qtd 2, não 2 subitems qtd 1).
      // Cupom, painel e relatórios já leem `quantidade`.
      const counts = new Map();
      sel.forEach(pid => {
        const k = String(pid);
        counts.set(k, (counts.get(k) || 0) + 1);
      });
      // Valor de cada linha conforme o tipo da etapa (Soma, Máximo, Subtração).
      const escolhidas = [...counts].map(([pid, qtd]) => ({ pid, qtd, item: step.items.find(i => String(i.product_id) === String(pid)) })).filter(e => e.item);
      const precosDaEtapa = linhasDaEtapa(step, escolhidas.map(e => ({ preco: getItemPrice(e.item, step), qtd: e.qtd })));
      escolhidas.forEach(({ pid, qtd, item }, posicao) => {
        const p = productById(pid);
        subitems.push({
          product_id: pid,
          nome: getItemName(item),
          // `preco` = TOTAL DA LINHA (unitário x quantidade). É a convenção do
          // sistema — o fluxo não-pizza (getItemSubitemsHelper) grava assim, e
          // cupons/ESC-POS/repetir-pedido somam `preco` direto. Gravar unitário
          // aqui subcobraria em todo mundo que não lê `quantidade`.
          preco: precosDaEtapa[posicao],
          quantidade: qtd,
          step_name: step.name || `Etapa ${idx + 1}`,
          category: p?.category || null,
        });
      });
    });
    const headlineName = flavorNames.length === 1
      ? `Pizza ${selectedSize.name} · ${flavorNames[0]}`
      : `Pizza ${selectedSize.name} · ${flavorNames.length} sabores`;
    onAddToCart({
      id: primaryFlavor?.id,
      name: headlineName,
      nome: headlineName,
      photo_url: primaryFlavor?.photo_url,
      quantity,
      preco: totalUnit,
      sale_price: totalUnit,
      final_price: totalUnit,
      product_type: "pizza",
      observation: observation.trim() || null,
      subitems,
      pizza: {
        size_id: selectedSize.id,
        size_name: selectedSize.name,
        flavor_ids: selectedFlavorIds,
        flavor_names: flavorNames,
        border_id: selectedBorderId || null,
        border_name: borderInfo?.name || null,
        flavors_price: Math.round(flavorsPrice * 100) / 100,
        border_price: Math.round(borderPrice * 100) / 100,
        pricing_rule: pricingRule,
      },
    });
    onClose();
  };

  if (!open) return null;

  const isFirstStep = stepIdx === 0;
  const isLastStep = stepIdx === stepsDef.length - 1;
  const stepNum = stepIdx + 1;
  const totalSteps = stepsDef.length;

  // stopOutside: barra propagação de pointerdown/mousedown pra Radix Dialogs ancestrais
  // não detectarem como "outside click" (fechariam junto). Radix usa addEventListener
  // nativo no document, então React stopPropagation não basta — precisa stopImmediatePropagation
  // do evento nativo pra evitar que o listener do Radix seja invocado.
  const stopOutside = (e) => {
    e.stopPropagation();
    if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === "function") {
      e.nativeEvent.stopImmediatePropagation();
    }
  };
  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-4"
      style={{ pointerEvents: "auto" }}
      onPointerDown={stopOutside}
      onMouseDown={stopOutside}
      onClick={onClose}
    >
      <div
        className={`${t.bgCard} w-full max-w-lg rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden`}
        onPointerDown={stopOutside}
        onMouseDown={stopOutside}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header com nome do sabor + etapa */}
        <div className={`p-3 border-b ${t.border}`}>
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={goBack}
              className={`w-8 h-8 rounded-full ${darkMode ? "bg-zinc-700 hover:bg-zinc-600" : "bg-gray-100 hover:bg-gray-200"} flex items-center justify-center transition-colors`}
              title={isFirstStep ? "Fechar" : "Voltar"}
            >
              <ChevronLeft className={`w-5 h-5 ${t.text}`} />
            </button>
            <div className="text-center">
              <p className={`text-[10px] ${t.textMuted} leading-tight`}>Etapa {stepNum} de {totalSteps}</p>
              <h2 className={`text-base font-bold ${t.text} leading-tight`}>{primaryFlavor?.name}</h2>
              {/* Badge de origem (debug). Aparece SÓ com ?_pizza_debug=1 na URL.
                  Útil pra confirmar se o popup carregou do PERSONALIZAÇÃO (order_steps
                  do produto) ou do CATÁLOGO universal (legado). */}
              {typeof window !== "undefined" && window.location.search.includes("_pizza_debug") && (
                <span className={`inline-block mt-1 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  config?.source === "product_steps"
                    ? "bg-green-500/15 text-green-600 dark:text-green-400 border border-green-500/30"
                    : "bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30"
                }`}>
                  Fonte: {config?.source === "product_steps" ? "Personalização (order_steps)" : "Catálogo legado"}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className={`w-8 h-8 rounded-full ${darkMode ? "bg-zinc-700 hover:bg-zinc-600" : "bg-gray-100 hover:bg-gray-200"} flex items-center justify-center transition-colors`}
            >
              <X className={`w-5 h-5 ${t.text}`} />
            </button>
          </div>
          {/* Progress bar */}
          <div className="flex gap-1 mt-2">
            {stepsDef.map((s, i) => (
              <div
                key={s.id}
                className={`flex-1 h-1 rounded-full transition-colors ${
                  i < stepIdx ? "bg-orange-500" : i === stepIdx ? "bg-orange-500" : (darkMode ? "bg-zinc-700" : "bg-gray-200")
                }`}
              />
            ))}
          </div>
          <h3 className={`text-center text-sm font-bold ${t.text} mt-3 uppercase tracking-wide`}>
            {currentStep?.label}
          </h3>
          {currentStep?.id === "size" && (
            <p className={`text-center text-[12px] ${t.textMuted} mt-1`}>
              Escolha o tamanho da sua pizza
            </p>
          )}
          {currentStep?.id === "flavors" && selectedSize && (
            <p className={`text-center text-[12px] ${t.textMuted} mt-1`}>
              Escolha até {maxFlavors} {maxFlavors === 1 ? "sabor" : "sabores"} · {selectedFlavorIds.length} / {maxFlavors}
            </p>
          )}
          {currentStep?.id === "border" && (
            <p className={`text-center text-[12px] ${t.textMuted} mt-1`}>
              Quer uma borda recheada? (opcional)
            </p>
          )}
          {currentStep?.id?.startsWith("custom-") && (() => {
            const step = customSteps[currentStep.customIdx];
            if (!step) return null;
            const min = stepMin(step);
            const max = stepMax(step);
            const maxLabel = max === Infinity ? "∞" : max;
            const sel = stepSelections[currentStep.customIdx] || [];
            let texto;
            if (max === Infinity && min === 0) {
              texto = "Escolha quantas quiser (opcional)";
            } else if (max === Infinity) {
              texto = `Escolha no mínimo ${min} ${min === 1 ? "opção" : "opções"}`;
            } else if (min === max) {
              texto = `Escolha ${min} ${min === 1 ? "opção" : "opções"}`;
            } else if (min === 0) {
              texto = `Escolha até ${max} ${max === 1 ? "opção" : "opções"} (opcional)`;
            } else {
              texto = `Escolha entre ${min} e ${max} opções`;
            }
            return (
              <p className={`text-center text-[12px] ${t.textMuted} mt-1`}>
                {texto} · {sel.length} / {maxLabel}
              </p>
            );
          })()}
          {currentStep?.id === "review" && (
            <p className={`text-center text-[12px] ${t.textMuted} mt-1`}>
              Confirme e adicione ao carrinho
            </p>
          )}
        </div>

        {/* Body — conteúdo por etapa */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* ETAPA 1: TAMANHO — cards limpos sem foto, foco em info */}
          {currentStep?.id === "size" && (
            <div className="space-y-2.5">
              {sizes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nenhum tamanho configurado
                </p>
              ) : sizes.map(s => {
                const priceAt = Number(primaryFlavor?.pizza_prices?.[s.id] || 0);
                const priceOrig = Number(primaryFlavor?.pizza_prices_original?.[s.id] || 0);
                const emOferta = priceOrig > priceAt + 0.004;
                const selected = s.id === selectedSizeId;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSelectedSizeId(s.id)}
                    data-e2e="pizza-tamanho" data-nome={s.name}
                    className={`relative w-full flex items-center gap-3 rounded-2xl border-2 p-4 transition-all text-left ${
                      selected
                        ? "border-orange-500 bg-orange-500/[0.06] shadow-md shadow-orange-500/10"
                        : `${t.border} hover:border-orange-300`
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={`font-bold text-lg ${t.text} leading-tight`}>{s.name}</p>
                      <p className={`text-[12px] ${t.textMuted} leading-snug mt-0.5`}>
                        {s.slices ? `${s.slices} fatias · ` : ""}até {s.max_flavors} sabor{s.max_flavors > 1 ? "es" : ""}
                      </p>
                      {s.description && (
                        <p className={`text-[11px] ${t.textMuted2} leading-snug mt-0.5`}>{s.description}</p>
                      )}
                      {priceAt > 0 && (
                        <p className="text-lg font-black text-orange-500 mt-1.5 tabular-nums leading-none flex items-baseline gap-2 flex-wrap">
                          {emOferta && (
                            <span className={`text-xs font-semibold line-through ${t.textMuted}`}>{fmtR$(priceOrig)}</span>
                          )}
                          {fmtR$(priceAt)}
                          {emOferta && primaryFlavor?.promo_id === "daily_offers" && (
                            <span className="text-[10px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground" data-e2e="pizza-sua-oferta">
                              Sua oferta
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                        selected ? "border-orange-500 bg-orange-500" : "border-muted-foreground/40"
                      }`}>
                        {selected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3.5} />}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* ETAPA 2: SABORES — sabor clicado no cardápio sempre vem em primeiro */}
          {currentStep?.id === "flavors" && selectedSize && (
            <div className="space-y-2.5">
              {(() => {
                const primary = primaryFlavor?.id;
                if (!primary) return allFlavors;
                return [
                  ...allFlavors.filter(f => f.id === primary),
                  ...allFlavors.filter(f => f.id !== primary),
                ];
              })().map(f => {
                const checked = selectedFlavorIds.includes(f.id);
                const priceAt = Number(f?.pizza_prices?.[selectedSizeId] || 0);
                const priceOrig = Number(f?.pizza_prices_original?.[selectedSizeId] || 0);
                const disabled = !checked && selectedFlavorIds.length >= maxFlavors;
                return (
                  <button
                    key={f.id}
                    onClick={() => toggleFlavor(f.id)}
                    data-e2e="pizza-sabor" data-nome={f.name}
                    disabled={disabled}
                    className={`w-full flex items-start gap-3 rounded-2xl border-2 p-3 transition-all text-left ${
                      checked
                        ? "border-orange-500 bg-orange-500/[0.06] shadow-md shadow-orange-500/10"
                        : disabled
                          ? `${t.border} opacity-50 cursor-not-allowed`
                          : `${t.border} hover:border-orange-300`
                    }`}
                  >
                    {/* Foto fixa quadrada à esquerda — não estica com o texto */}
                    <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-xl ${darkMode ? "bg-zinc-700" : "bg-gray-100"} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                      {f.photo_url ? (
                        <img src={fotoPequena(f.photo_url)} alt={f.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <Pizza className={`w-8 h-8 ${t.textMuted2}`} />
                      )}
                    </div>
                    {/* Conteúdo central — descrição cresce livremente */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-bold text-base ${t.text} leading-tight`}>{f.name}</p>
                      {f.description && (
                        <p className={`text-[11px] ${t.textMuted} leading-snug mt-1 break-words`}>{f.description}</p>
                      )}
                      {/* Linha de baixo: fração + preço */}
                      <div className="flex items-baseline gap-2 mt-2">
                        {checked && selectedFlavorIds.length > 1 && (
                          <span className="text-sm font-black text-orange-500 tabular-nums leading-none">
                            {fracLabel(selectedFlavorIds.length)}
                          </span>
                        )}
                        {priceOrig > priceAt + 0.004 && (
                          <span className={`text-[11px] font-semibold tabular-nums leading-none line-through ${t.textMuted}`}>
                            {fmtR$(priceOrig)}
                          </span>
                        )}
                        <span className={`text-sm font-bold tabular-nums leading-none ${checked ? "text-orange-500" : t.textMuted}`}>
                          {fmtR$(priceAt)}
                        </span>
                      </div>
                    </div>
                    {/* Checkbox circular à direita */}
                    <div className="flex-shrink-0 pt-1">
                      <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                        checked ? "border-orange-500 bg-orange-500" : "border-muted-foreground/40"
                      }`}>
                        {checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3.5} />}
                      </span>
                    </div>
                  </button>
                );
              })}
              {selectedFlavorIds.length > 1 && (
                <p className={`text-[10px] ${t.textMuted} text-center mt-2`}>
                  Regra de preço: <b>{pricingRule === "average" ? "média dos sabores" : "sabor mais caro"}</b>
                </p>
              )}
            </div>
          )}

          {/* ETAPA 3: BORDA */}
          {currentStep?.id === "border" && (
            <div className="space-y-1.5">
              <button
                onClick={() => setSelectedBorderId(null)}
                className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-all text-left ${
                  !selectedBorderId ? "border-orange-500 bg-orange-500/[0.06]" : `${t.border} hover:border-orange-300`
                }`}
              >
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${!selectedBorderId ? "border-orange-500 bg-orange-500" : "border-muted-foreground/40"}`}>
                  {!selectedBorderId && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
                </span>
                <span className={`text-sm font-semibold ${t.text} flex-1`}>Sem borda</span>
                <span className={`text-xs ${t.textMuted}`}>—</span>
              </button>
              {borders.map(b => {
                const checked = b.id === selectedBorderId;
                const priceAt = Number(b?.prices?.[selectedSizeId] || 0);
                return (
                  <button
                    key={b.id}
                    onClick={() => setSelectedBorderId(b.id)}
                    data-e2e="pizza-borda" data-nome={b.name}
                    className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-all text-left ${
                      checked ? "border-orange-500 bg-orange-500/[0.06]" : `${t.border} hover:border-orange-300`
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${checked ? "border-orange-500 bg-orange-500" : "border-muted-foreground/40"}`}>
                      {checked && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-sm ${t.text}`}>{b.name}</p>
                      {b.description && <p className={`text-[10px] ${t.textMuted}`}>{b.description}</p>}
                    </div>
                    <span className={`text-xs font-bold tabular-nums flex-shrink-0 ${checked ? "text-orange-500" : t.textMuted}`}>
                      {priceAt > 0 ? `+${fmtR$(priceAt)}` : "grátis"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* ETAPAS CUSTOM (order_steps do produto) — Adicionais/Acompanhamentos/etc. */}
          {currentStep?.id?.startsWith("custom-") && (() => {
            const step = customSteps[currentStep.customIdx];
            if (!step) return null;
            const sel = stepSelections[currentStep.customIdx] || [];
            const max = stepMax(step);
            const single = isSingleChoice(step);
            const visibleItems = (step.items || []).filter(it => isItemAvailable(it));
            return (
              <div className="space-y-2">
                {visibleItems.length === 0 ? (
                  <p className={`text-sm ${t.textMuted} text-center py-4`}>
                    Nenhum item disponível nesta etapa
                  </p>
                ) : visibleItems.map((it) => {
                  const checked = sel.includes(it.product_id);
                  // Em single-choice (radio) nunca desabilita — click substitui a seleção
                  const disabled = !checked && !single && max !== Infinity && sel.length >= max;
                  const itemPrice = getItemPrice(it, step);
                  const itemName = getItemName(it);
                  const itemPhoto = getItemPhoto(it);
                  // v1.5.399 — item com allow_quantity vira stepper (−/qty/+)
                  const comQtd = allowsQty(step, it);
                  const qtd = comQtd ? qtyOf(currentStep.customIdx, it.product_id) : (checked ? 1 : 0);
                  const maxDoItem = itemMaxQty(it);
                  const podeMais = qtd < maxDoItem && (max === Infinity || sel.length < max);
                  // Com stepper o wrapper vira <div> (não dá pra aninhar button
                  // dentro de button), mas a linha INTEIRA continua clicável:
                  // toque em qualquer área adiciona 1 — mesma afordância de antes.
                  const Wrapper = comQtd ? "div" : "button";
                  const selecionado = comQtd ? qtd > 0 : checked;
                  const bloqueado = comQtd ? !podeMais && qtd === 0 : disabled;
                  return (
                    <Wrapper
                      key={it.product_id}
                      {...(comQtd
                        ? {
                            role: "group",
                            "aria-label": `${itemName}${qtd > 0 ? ` — ${qtd} selecionado(s)` : ""}`,
                            onClick: () => podeMais && incStepItem(currentStep.customIdx, it.product_id),
                          }
                        : { onClick: () => toggleStepItem(currentStep.customIdx, it.product_id), disabled })}
                      className={`w-full flex items-center gap-3 rounded-2xl border-2 p-3 transition-all text-left ${comQtd && podeMais ? "cursor-pointer" : ""} ${
                        selecionado
                          ? "border-orange-500 bg-orange-500/[0.06] shadow-md shadow-orange-500/10"
                          : bloqueado
                            ? `${t.border} opacity-50 cursor-not-allowed`
                            : `${t.border} hover:border-orange-300`
                      }`}
                    >
                      <div className={`w-14 h-14 rounded-xl ${darkMode ? "bg-zinc-700" : "bg-gray-100"} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                        {itemPhoto ? (
                          <img
                            src={fotoPequena(itemPhoto)}
                            alt={itemName}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <ImageOff className={`w-5 h-5 ${t.textMuted2}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-sm ${t.text} leading-tight`}>{itemName}</p>
                        {getItemDescription(it) && (
                          <p className={`text-[11px] ${t.textMuted} leading-snug mt-0.5 line-clamp-2`}>
                            {getItemDescription(it)}
                          </p>
                        )}
                        <p className={`text-xs font-bold tabular-nums mt-1 ${(comQtd ? qtd > 0 : checked) ? "text-orange-500" : t.textMuted}`}>
                          {itemPrice > 0 ? `${sinalDaEtapa(step)}${fmtR$(itemPrice)}` : "grátis"}
                          {comQtd && qtd > 1 && itemPrice > 0 && (
                            <span className={`ml-1 font-semibold ${t.textMuted}`}>
                              x {qtd} = {fmtR$(itemPrice * qtd)}
                            </span>
                          )}
                        </p>
                      </div>
                      {comQtd ? (
                        /* Stepper de quantidade (v1.5.399) */
                        qtd === 0 ? (
                          <button
                            type="button"
                            // stopPropagation OBRIGATÓRIO: este botão vive dentro
                            // do wrapper que também incrementa ao clique — sem
                            // isso o clique contava 2 unidades de uma vez.
                            onClick={(e) => {
                              e.stopPropagation();
                              incStepItem(currentStep.customIdx, it.product_id);
                            }}
                            disabled={!podeMais}
                            className={`px-3 h-9 rounded-xl border-2 font-bold text-sm flex-shrink-0 transition-colors ${
                              podeMais
                                ? "border-orange-500 text-orange-500 hover:bg-orange-500/10"
                                : `${t.border} ${t.textMuted2} opacity-50 cursor-not-allowed`
                            }`}
                          >
                            Adicionar
                          </button>
                        ) : (
                          <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => decStepItem(currentStep.customIdx, it.product_id)}
                              className="w-9 h-9 rounded-xl border-2 border-orange-500 text-orange-500 font-black text-lg leading-none flex items-center justify-center hover:bg-orange-500/10 transition-colors"
                              aria-label={`Remover 1 ${itemName}`}
                            >
                              −
                            </button>
                            <span className={`w-7 text-center font-black tabular-nums ${t.text}`}>{qtd}</span>
                            <button
                              type="button"
                              onClick={() => incStepItem(currentStep.customIdx, it.product_id)}
                              disabled={!podeMais}
                              className={`w-9 h-9 rounded-xl border-2 font-black text-lg leading-none flex items-center justify-center transition-colors ${
                                podeMais
                                  ? "border-orange-500 bg-orange-500 text-white hover:bg-orange-600"
                                  : `${t.border} ${t.textMuted2} opacity-50 cursor-not-allowed`
                              }`}
                              aria-label={`Adicionar 1 ${itemName}`}
                            >
                              +
                            </button>
                          </div>
                        )
                      ) : (
                        <span className={`w-5 h-5 ${single ? "rounded-full" : "rounded-md"} border-2 flex items-center justify-center flex-shrink-0 ${
                          checked ? "border-orange-500 bg-orange-500" : "border-muted-foreground/40"
                        }`}>
                          {checked && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
                        </span>
                      )}
                    </Wrapper>
                  );
                })}
                {step.calculation_type === "maior" && sel.length > 1 && (
                  <p className={`text-[10px] ${t.textMuted} text-center mt-2`}>
                    Cobra apenas o item mais caro selecionado
                  </p>
                )}
              </div>
            );
          })()}

          {/* ETAPA FINAL: RESUMO + OBSERVAÇÃO */}
          {currentStep?.id === "review" && (
            <div className="space-y-3">
              {/* Foto do produto em destaque */}
              {primaryFlavor?.photo_url && (
                <div className={`rounded-2xl overflow-hidden ${darkMode ? "bg-zinc-700" : "bg-gray-100"} relative`} style={{ aspectRatio: "16/9" }}>
                  <img
                    src={getImageUrl(primaryFlavor.photo_url)}
                    alt={primaryFlavor.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-white font-bold text-base leading-tight">{primaryFlavor.name}</p>
                    {primaryFlavor.description && (
                      <p className="text-white/90 text-[11px] leading-snug mt-0.5 line-clamp-2">{primaryFlavor.description}</p>
                    )}
                  </div>
                </div>
              )}
              {/* Resumo */}
              <div className={`rounded-2xl border-2 border-orange-500/40 ${darkMode ? "bg-orange-500/[0.04]" : "bg-orange-500/[0.06]"} p-4 space-y-2`}>
                {/* TAMANHO — mostra o valor final da pizza (flavorsPrice, regra aplicada).
                    Quem define o preço da pizza é o tamanho + regra (maior/menor/média),
                    não cada sabor individualmente. */}
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <p className={`text-[10px] uppercase tracking-wider font-bold ${t.textMuted}`}>Tamanho</p>
                    <p className={`text-sm font-bold ${t.text}`}>{selectedSize?.name}</p>
                  </div>
                  <span className={`text-sm font-bold tabular-nums ${t.text}`}>{fmtR$(flavorsPrice)}</span>
                </div>
                <div className={`border-t ${t.border} pt-2`}>
                  <p className={`text-[10px] uppercase tracking-wider font-bold ${t.textMuted} mb-1.5`}>
                    Sabores ({selectedFlavorIds.length})
                  </p>
                  <div className="space-y-1">
                    {selectedFlavorIds.map((fid) => {
                      const f = allFlavors.find(x => x.id === fid);
                      return (
                        <div key={fid} className={`text-sm ${t.text}`}>
                          {selectedFlavorIds.length > 1 && <span className="font-bold text-orange-500 mr-1.5">{fracLabel(selectedFlavorIds.length)}</span>}
                          {f?.name}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {borderInfo && (
                  <div className={`border-t ${t.border} pt-2`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] uppercase tracking-wider font-bold ${t.textMuted}`}>Borda</span>
                      <div className="text-right">
                        <span className={`text-sm font-semibold ${t.text}`}>{borderInfo.name}</span>
                        {borderPrice > 0 && <span className={`text-xs ${t.textMuted} ml-1.5 tabular-nums`}>+{fmtR$(borderPrice)}</span>}
                      </div>
                    </div>
                  </div>
                )}
                {/* Resumo das etapas custom (Adicionais/Acompanhamentos/etc.) */}
                {customSteps.map((step, idx) => {
                  const sel = stepSelections[idx] || [];
                  if (sel.length === 0) return null;
                  return (
                    <div key={`sum-custom-${idx}`} className={`border-t ${t.border} pt-2`}>
                      <p className={`text-[10px] uppercase tracking-wider font-bold ${t.textMuted} mb-1.5`}>
                        {step.name || `Etapa ${idx + 1}`} ({sel.length})
                      </p>
                      <div className="space-y-1">
                        {/* Consolida repetições em "2x Item" (v1.5.399) */}
                        {[...sel.reduce((m, pid) => m.set(String(pid), (m.get(String(pid)) || 0) + 1), new Map())].map(([pid, qtd]) => {
                          const item = step.items.find(i => String(i.product_id) === String(pid));
                          if (!item) return null;
                          const price = getItemPrice(item, step);
                          return (
                            <div key={pid} className="flex items-center justify-between">
                              <span className={`text-sm ${t.text}`}>
                                {qtd > 1 ? `${qtd}x ` : ""}{getItemName(item)}
                              </span>
                              <span className={`text-xs ${t.textMuted} tabular-nums`}>
                                {price > 0 ? `+${fmtR$(price * qtd)}` : "grátis"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Observação — destaque visual leve com borda+fundo amarelo
                  bem sutil pra chamar atenção sem competir com o resumo laranja */}
              <div className={`rounded-2xl border-2 border-amber-500/30 ${darkMode ? "bg-amber-500/[0.04]" : "bg-amber-500/[0.06]"} p-4`}>
                <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${darkMode ? "text-amber-300" : "text-amber-700"}`}>
                  Observação <span className={`font-normal normal-case ${t.textMuted2}`}>(opcional)</span>
                </h4>
                <Textarea
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  placeholder="Ex: sem cebola, bem assada..."
                  maxLength={300}
                  rows={2}
                  className="bg-background/60 border-amber-500/20 focus-visible:border-amber-500/60 focus-visible:ring-amber-500/20"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer fixo: qty + ação */}
        <div className={`p-3 border-t ${t.border} ${t.bgCard} space-y-2`}>
          {isLastStep && (
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 rounded-full border ${t.border} px-1`}>
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className={`w-8 h-8 rounded-full flex items-center justify-center ${t.text} hover:bg-muted`}>
                  <Minus className="w-4 h-4" />
                </button>
                <span className={`text-sm font-bold ${t.text} w-6 text-center tabular-nums`}>{quantity}</span>
                <button onClick={() => setQuantity(q => q + 1)} className={`w-8 h-8 rounded-full flex items-center justify-center ${t.text} hover:bg-muted`}>
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 text-right">
                <p className={`text-[10px] ${t.textMuted}`}>Total</p>
                <p className="text-lg font-bold text-orange-500 tabular-nums leading-tight">{fmtR$(totalCart)}</p>
              </div>
            </div>
          )}
          {!isLastStep && totalUnit > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className={t.textMuted}>Parcial:</span>
              <span className="font-bold text-orange-500 tabular-nums">{fmtR$(totalUnit)}</span>
            </div>
          )}
          {isLastStep ? (
            <Button
              onClick={handleAdd}
              data-e2e="pizza-adicionar"
              disabled={!selectedSize || selectedFlavorIds.length === 0 || totalUnit <= 0}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base shadow-lg disabled:opacity-50"
            >
              Adicionar ao carrinho · {fmtR$(totalCart)}
            </Button>
          ) : (
            <Button
              onClick={goNext}
              data-e2e="pizza-avancar"
              disabled={!canAdvance}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              Avançar
              <ChevronRight className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
